import AppHeader from "@/components/AppHeader";
import {
  DashboardEditFields,
  canSaveDashboardEdit,
  mapToTags,
  type TagMap,
} from "@/components/DashboardEditFields";
import { useCapabilities } from "@/hooks/useCapabilities";
import { useAddDashboard, useSetDashboardTags, type ApiDashboard } from "@/hooks/useDashboards";
import { useProperties, useUpdateProperty } from "@/hooks/useProperties";
import { useTenant } from "@/hooks/useTenant";
import { useUserGroups } from "@/hooks/useUserGroups";
import { toggleGroupSelection } from "@/lib/addDashboardHelpers";
import { Button, Checkbox, Label } from "@simplicity/ui";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router";

// SIM-1421: composes the shared `DashboardEditFields` body so the form picks up tenant Properties
// and the "+ Add option…" UX without duplicating rendering. SIM-1464: year and category read their
// options from PropertyDef, and the shared `onAddPropertyOption` callback PATCHes the matching
// PropertyDef for those two and for arbitrary properties alike.

interface UserGroup {
  id: string;
  name: string;
}

function UserGroupsSelector(props: {
  userGroups: UserGroup[];
  selectedGroupIds: string[];
  toggleGroup: (groupId: string) => void;
}) {
  if (props.userGroups.length === 0) return null;
  return (
    <fieldset className="m-0 flex flex-col gap-2 border-0 p-0">
      <legend className="text-xs font-medium">Assign to User Groups</legend>
      <div className="border border-border rounded p-3 flex flex-col gap-2 max-h-40 overflow-y-auto">
        {props.userGroups.map((group) => (
          <Label
            key={group.id}
            className="flex items-center gap-2 text-sm text-foreground cursor-pointer"
          >
            <Checkbox
              checked={props.selectedGroupIds.includes(group.id)}
              onCheckedChange={() => props.toggleGroup(group.id)}
            />
            {group.name}
          </Label>
        ))}
      </div>
    </fieldset>
  );
}

function useAddDashboardSubmit(opts: {
  title: string;
  url: string;
  schoolYear: string;
  category: string;
  tagMap: TagMap;
  selectedGroupIds: string[];
}) {
  const navigate = useNavigate();
  const { tenantPath } = useTenant();
  const addDashboard = useAddDashboard();
  const setDashboardTags = useSetDashboardTags();
  const onSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!canSaveDashboardEdit({ title: opts.title, url: opts.url, type: "looker" })) return;
      const created = (await addDashboard.mutateAsync({
        title: opts.title,
        url: opts.url,
        school_year: opts.schoolYear,
        category: opts.category,
        groupIds: opts.selectedGroupIds.length > 0 ? opts.selectedGroupIds : undefined,
      })) as ApiDashboard;

      // Year + category are persisted by the create handler. Everything
      // else flows through the dedicated tags PUT.
      const nonLegacy = Object.fromEntries(
        Object.entries(opts.tagMap).filter(([k]) => k !== "school_year" && k !== "category"),
      );
      if (Object.values(nonLegacy).some((vs) => vs.length > 0)) {
        await setDashboardTags.mutateAsync({
          dashboardId: created.id,
          tags: mapToTags(nonLegacy),
        });
      }

      void navigate(tenantPath("/"));
    },
    [addDashboard, setDashboardTags, navigate, tenantPath, opts],
  );
  return { onSubmit, isPending: addDashboard.isPending };
}

/**
 * SIM-3113: the Add Dashboard cards are super-admin only, but `add-dashboard`
 * is a globally registered route — without this a tenant admin could reach the
 * form by typing the URL, fill it in, and only find out when the POST 403'd.
 * Returns true once the role has resolved and the user may not be here.
 */
function useRedirectUnlessSuperAdmin(): boolean {
  const navigate = useNavigate();
  const { tenantPath } = useTenant();
  const { canManageDashboards, isLoading } = useCapabilities();
  const blocked = !isLoading && !canManageDashboards;
  useEffect(() => {
    if (blocked) void navigate(tenantPath("/"), { replace: true });
  }, [blocked, navigate, tenantPath]);
  return blocked;
}

const AddDashboard = () => {
  const navigate = useNavigate();
  const { tenantPath } = useTenant();
  const blockedFromRoute = useRedirectUnlessSuperAdmin();
  const [title, setTitle] = useState("");
  const [url, setUrl] = useState("");
  const [schoolYear, setSchoolYear] = useState("");
  const [category, setCategory] = useState("");
  const [tagMap, setTagMap] = useState<TagMap>({});
  const [selectedGroupIds, setSelectedGroupIds] = useState<string[]>([]);

  const updateProperty = useUpdateProperty();
  const { data: properties } = useProperties();
  const { data: userGroups = [] } = useUserGroups();

  // SIM-1421 / SIM-1464 — single inline-add path: every property (year,
  // category, and arbitrary user-defined keys) PATCHes the matching
  // PropertyDef's options array. <DashboardEditFields> owns the dialog UX
  // and the "auto-select the newly added value" follow-up.
  const handleAddPropertyOption = useCallback(
    async (propertyKey: string, newValue: string) => {
      const prop = properties.find((p) => p.key === propertyKey);
      if (!prop) return;
      await updateProperty.mutateAsync({
        id: prop.id,
        options: [...prop.options, newValue],
      });
    },
    [properties, updateProperty],
  );

  const submit = useAddDashboardSubmit({
    title,
    url,
    schoolYear,
    category,
    tagMap,
    selectedGroupIds,
  });

  const toggleGroup = (groupId: string) => {
    setSelectedGroupIds((prev) => toggleGroupSelection(prev, groupId));
  };

  const canSave = canSaveDashboardEdit({ title, url, type: "looker" });

  if (blockedFromRoute) return null;

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <AppHeader />
      <div className="flex-1 flex items-start justify-center pt-16">
        <form
          onSubmit={(e) => {
            void submit.onSubmit(e);
          }}
          className="w-full max-w-md flex flex-col gap-4"
        >
          <h2 className="text-lg font-semibold mb-2">Add Dashboard</h2>
          <DashboardEditFields
            dashboardId="new"
            dashboardType="looker"
            editTitle={title}
            setEditTitle={setTitle}
            editUrl={url}
            setEditUrl={setUrl}
            editSchoolYear={schoolYear}
            setEditSchoolYear={setSchoolYear}
            editCategory={category}
            setEditCategory={setCategory}
            properties={properties}
            tagMap={tagMap}
            setTagMap={setTagMap}
            variant="modal"
            onAddPropertyOption={handleAddPropertyOption}
          />
          <UserGroupsSelector
            userGroups={userGroups}
            selectedGroupIds={selectedGroupIds}
            toggleGroup={toggleGroup}
          />
          <div className="flex gap-3 mt-4">
            <Button type="submit" disabled={!canSave || submit.isPending}>
              {submit.isPending ? "Adding..." : "Add Dashboard"}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                void navigate(tenantPath("/"));
              }}
            >
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddDashboard;
